import React, { useState, useEffect, useRef } from 'react';
import { clientApi, formatApiError } from '../../context/ClientAuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Signature, CheckCircle, Clock, FilePdf, DownloadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function ClientSignature() {
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const fetchStatus = async () => {
    try {
      const { data } = await clientApi.get('/onboarding-status');
      setOnboardingStatus(data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (canvasRef.current && onboardingStatus?.has_loe && !onboardingStatus?.has_signature) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  }, [onboardingStatus]);

  const handleDownloadLoe = async () => {
    if (!onboardingStatus?.loe_file_path) return;
    try {
      const baseUrl = clientApi.defaults.baseURL.replace('/client-portal', '');
      const response = await clientApi.get(`${baseUrl}/files/${onboardingStatus.loe_file_path}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', onboardingStatus.loe_filename || 'Letter_of_Engagement.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download letter');
    }
  };

  // Signature canvas functions
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const saveSignature = async () => {
    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL('image/png');

    setSigning(true);
    try {
      await clientApi.post('/signature', { signature: signatureData });
      toast.success('Signature saved! Your onboarding is now complete.');
      fetchStatus();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-signature-page">
      <div>
        <h1 className="heading-2 mb-1">Sign Documents</h1>
        <p className="text-muted-foreground">Sign the Letter of Engagement to complete your onboarding</p>
      </div>

      {/* Already Signed */}
      {onboardingStatus?.has_signature && (
        <Card className="border border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-green-100">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="heading-4 text-green-800">Document Signed</h3>
                <p className="text-green-700 text-sm">
                  You have already signed the Letter of Engagement. Your onboarding is complete.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No LOE yet */}
      {!onboardingStatus?.has_loe && (
        <Card className="border border-yellow-300 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-yellow-100">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <h3 className="heading-4 text-yellow-800">Waiting for Letter of Engagement</h3>
                <p className="text-yellow-700 text-sm">
                  Your accountant has not yet uploaded the Letter of Engagement. Please check back later or contact them.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Signature Pad */}
      {onboardingStatus?.has_loe && !onboardingStatus?.has_signature && (
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="heading-4 flex items-center gap-2">
              <Signature className="w-5 h-5" />
              Sign Letter of Engagement
            </CardTitle>
            <CardDescription>
              Please review the letter below, then sign to confirm you agree to the terms
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Download/View Letter */}
            {onboardingStatus?.loe_file_path && (
              <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex items-center gap-3">
                  <FilePdf className="w-6 h-6 text-blue-600" />
                  <div>
                    <p className="font-medium text-sm text-blue-900">{onboardingStatus.loe_filename || 'Document for Signing'}</p>
                    <p className="text-xs text-blue-600">Please review before signing</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadLoe}
                  data-testid="download-loe-btn"
                >
                  <DownloadSimple className="w-4 h-4 mr-1" />
                  View / Download
                </Button>
              </div>
            )}

            <div className="p-4 bg-muted/50 rounded-md border border-border">
              <p className="text-sm text-muted-foreground">
                <strong>Important:</strong> By signing below, you acknowledge that you have read, understood, 
                and agree to the terms outlined in the Letter of Engagement provided by your accountant.
              </p>
            </div>

            <div className="border border-border rounded-md overflow-hidden">
              <canvas
                ref={canvasRef}
                width={600}
                height={200}
                className="signature-canvas w-full bg-white cursor-crosshair"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                data-testid="client-signature-canvas"
              />
            </div>
            
            <p className="text-sm text-muted-foreground text-center">
              Sign above using your mouse or touch screen
            </p>

            <div className="flex gap-3">
              <Button variant="outline" onClick={clearSignature} data-testid="clear-signature-btn">
                Clear
              </Button>
              <Button
                onClick={saveSignature}
                disabled={signing}
                className="flex-1"
                data-testid="save-signature-btn"
              >
                {signing ? 'Saving...' : 'Sign & Submit'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="border border-border bg-muted/50">
        <CardContent className="py-4">
          <h4 className="font-medium mb-2">What is the Letter of Engagement?</h4>
          <p className="text-sm text-muted-foreground">
            The Letter of Engagement is a formal agreement between you and your accountant that outlines 
            the scope of services, responsibilities, and terms of the professional relationship. 
            Signing this document is required to complete your client onboarding.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
